'use strict';

/** @typedef {import('../pipelineStatusTypes').PipelineStatusStage} PipelineStatusStage */

import '@xyflow/react/dist/style.css';

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
	Background,
	Controls,
	MiniMap,
	MarkerType,
	Position,
	ReactFlow,
	ReactFlowProvider,
	useEdgesState,
	useNodesState,
	useReactFlow,
} from '@xyflow/react';
import { Box, useTheme } from '@mui/material';
import {
	buildPipelineStatusGraphEdgesFromDependsOn,
	mapPipelineStatusStagesToGraphNodes,
	PIPELINE_STATUS_STAGE_NODE_TYPE,
} from './pipelineStatusGraphLayout';
import {
	PIPELINE_STATUS_EDGE_TYPE,
	pipelineStatusEdgePaintRank,
	resolvePipelineStatusAccentColor,
} from './pipelineStatusGraphEdge';
import { PipelineStatusDrawer } from './PipelineStatusDrawer';
import { PipelineStatusEdge } from './PipelineStatusEdge';
import { PipelineStatusStageNode } from './PipelineStatusStageNode';

/** @param {{ fitKey: string }} props */
function GraphFitView({ fitKey }) {
	const { fitView } = useReactFlow();
	useEffect(() => {
		const id = requestAnimationFrame(() => {
			fitView({ padding: 0.18, duration: 200 });
		});
		return () => cancelAnimationFrame(id);
	}, [fitView, fitKey]);
	return null;
}

const nodeTypes = { [PIPELINE_STATUS_STAGE_NODE_TYPE]: PipelineStatusStageNode };
const edgeTypes = { [PIPELINE_STATUS_EDGE_TYPE]: PipelineStatusEdge };

/**
 * @param {{ stages: PipelineStatusStage[]; highlightedStageIds?: string[] }} props
 */
function PipelineStatusGraphInner({ stages, highlightedStageIds }) {
	const theme = useTheme();
	const [selected, setSelected] = useState(/** @type {PipelineStatusStage | null} */ (null));

	const highlightSet = useMemo(
		() => new Set(Array.isArray(highlightedStageIds) ? highlightedStageIds : []),
		[highlightedStageIds],
	);
	const baseNodes = useMemo(
		() =>
			mapPipelineStatusStagesToGraphNodes(stages).map((n) =>
				highlightSet.has(n.id)
					? { ...n, data: { ...n.data, graphHints: { ...n.data.graphHints, highlighted: true } } }
					: n,
			),
		[stages, highlightSet],
	);
	const [nodes, setNodes, onNodesChange] = useNodesState(baseNodes);
	useEffect(() => {
		setNodes(baseNodes);
	}, [baseNodes, setNodes]);

	const plainEdges = useMemo(() => buildPipelineStatusGraphEdgesFromDependsOn(stages), [stages]);
	const statusByTargetId = useMemo(() => {
		const m = new Map();
		for (const s of stages) m.set(s.id, s.status);
		return m;
	}, [stages]);

	const rfEdges = useMemo(
		() =>
			plainEdges
				.map((e) => {
					const status = statusByTargetId.get(e.target);
					const color = resolvePipelineStatusAccentColor(theme.palette, status);
					return {
						...e,
						type: PIPELINE_STATUS_EDGE_TYPE,
						sourcePosition: Position.Right,
						targetPosition: Position.Left,
						zIndex: pipelineStatusEdgePaintRank(status),
						animated: status === 'running',
						markerEnd: { type: MarkerType.ArrowClosed, color, width: 16, height: 16 },
						style: { stroke: color, strokeWidth: 1.5 },
					};
				})
				.sort((a, b) => a.zIndex - b.zIndex),
		[plainEdges, statusByTargetId, theme.palette],
	);

	const [edges, setEdges, onEdgesChange] = useEdgesState(rfEdges);
	useEffect(() => {
		setEdges(rfEdges);
	}, [rfEdges, setEdges]);

	const fitKey = useMemo(
		() => stages.map((s) => `${s.id}:${s.status}:${s.factId || ''}:${s.occurredAt || ''}`).join('|'),
		[stages]
	);

	const onNodeClick = useCallback((/** @type {unknown} */ _evt, /** @type {{ data?: { stage?: PipelineStatusStage } }} */ node) => {
		const st = node?.data?.stage;
		setSelected(st ?? null);
	}, []);

	const minimapNodeColor = useCallback((/** @type {{ data?: { stage?: PipelineStatusStage } }} */ n) => {
		const st = n?.data?.stage?.status;
		switch (st) {
			case 'success':
				return '#2e7d32';
			case 'failed':
				return '#c62828';
			case 'running':
				return '#1565c0';
			default:
				return '#78909c';
		}
	}, []);

	return (
		<>
			<Box
				data-testid="pipeline-progress-graph"
				sx={{ height: 420, width: '100%', borderRadius: 1, overflow: 'hidden', border: 1, borderColor: 'divider' }}
			>
				<ReactFlow
					nodes={nodes}
					edges={edges}
					onNodesChange={onNodesChange}
					onEdgesChange={onEdgesChange}
					nodeTypes={nodeTypes}
					edgeTypes={edgeTypes}
					onNodeClick={onNodeClick}
					nodesDraggable={false}
					nodesConnectable={false}
					edgesReconnectable={false}
					elementsSelectable
					proOptions={{ hideAttribution: true }}
					defaultEdgeOptions={{
						type: PIPELINE_STATUS_EDGE_TYPE,
						sourcePosition: Position.Right,
						targetPosition: Position.Left,
						markerEnd: { type: MarkerType.ArrowClosed, color: '#64748b', width: 16, height: 16 },
					}}
					minZoom={0.4}
					maxZoom={1.5}
				>
					<Background gap={20} size={1} color="#e2e8f0" />
					<Controls showInteractive={false} />
					<MiniMap
						nodeStrokeWidth={2}
						nodeColor={minimapNodeColor}
						maskColor="rgba(15, 23, 42, 0.06)"
						style={{ height: 72, width: 110 }}
					/>
					<GraphFitView fitKey={fitKey} />
				</ReactFlow>
			</Box>
			<PipelineStatusDrawer stage={selected} open={Boolean(selected)} onClose={() => setSelected(null)} />
		</>
	);
}

/**
 * DAG visualization for pipeline status stages (pure graph view of `PipelineStatusStage[]`).
 * `highlightedStageIds` (optional) emphasizes the nodes belonging to a selected high-level stage.
 * @param {{ stages: PipelineStatusStage[]; highlightedStageIds?: string[] }} props
 */
export function PipelineStatusGraph({ stages, highlightedStageIds }) {
	return (
		<ReactFlowProvider>
			<PipelineStatusGraphInner stages={stages} highlightedStageIds={highlightedStageIds} />
		</ReactFlowProvider>
	);
}
